import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import AppDialog from '../components/AppDialog';

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);

  const showAlert = (title, message) => setDialog({ title, message });
  const showConfirm = (title, message, onConfirm) =>
    setDialog({ title, message, onConfirm, danger: true, confirmText: 'Delete', cancelText: 'Cancel' });

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'players'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.wins - a.wins || b.points_scored - a.points_scored);
      setPlayers(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm({ name: '', email: '', phone: '' }); setShowModal(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, email: p.email || '', phone: p.phone || '' }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await updateDoc(doc(db, 'players', editing.id), { name: form.name, email: form.email || null, phone: form.phone || null });
      } else {
        await addDoc(collection(db, 'players'), {
          name: form.name,
          email: form.email || null,
          phone: form.phone || null,
          wins: 0, losses: 0, points_scored: 0, points_against: 0, matches_played: 0,
          created_at: new Date().toISOString(),
        });
      }
      await load();
      setShowModal(false);
    } catch (err) {
      showAlert('Error', err.message || 'Error saving player');
    } finally { setSaving(false); }
  };

  const handleDelete = (id) => {
    showConfirm('Delete Player', 'Are you sure you want to delete this player? This cannot be undone.', async () => {
      await deleteDoc(doc(db, 'players', id));
      setPlayers(prev => prev.filter(p => p.id !== id));
    });
  };

  const filtered = players.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const avatarHue = (id) => {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
    return h;
  };

  return (
    <div className="container" style={{ padding: '32px 20px' }}>
      <div className="page-header">
        <div>
          <h1>Players</h1>
          <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '4px' }}>{players.length} registered players</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Player</button>
      </div>

      <div className="card" style={{ marginBottom: '20px', padding: '16px 20px' }}>
        <input className="form-control" placeholder="Search players by name or email..."
          value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: '380px' }} />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#7f8c8d' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            <div style={{ fontSize: '14px' }}>Loading players…</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="icon">👤</div>
            <h3>No players yet</h3>
            <p>Add players to get started</p>
            <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={openAdd}>Add First Player</button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th><th>Name</th><th>Contact</th>
                <th style={{ textAlign: 'center' }}>W</th>
                <th style={{ textAlign: 'center' }}>L</th>
                <th style={{ textAlign: 'center' }}>Pts Scored</th>
                <th style={{ textAlign: 'center' }}>Matches</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const hue = avatarHue(p.id);
                return (
                  <tr key={p.id} className="animate-fadein">
                    <td style={{ color: '#7f8c8d', fontSize: '13px' }}>{i + 1}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: `hsl(${hue},60%,85%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '13px', color: `hsl(${hue},60%,35%)` }}>
                          {p.name.charAt(0).toUpperCase()}
                        </div>
                        <span style={{ fontWeight: '600' }}>{p.name}</span>
                      </div>
                    </td>
                    <td style={{ color: '#7f8c8d', fontSize: '13px' }}>
                      {p.email && <div>{p.email}</div>}
                      {p.phone && <div>{p.phone}</div>}
                      {!p.email && !p.phone && '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}><span style={{ color: '#27ae60', fontWeight: '700' }}>{p.wins}</span></td>
                    <td style={{ textAlign: 'center' }}><span style={{ color: '#e74c3c', fontWeight: '700' }}>{p.losses}</span></td>
                    <td style={{ textAlign: 'center', fontWeight: '600' }}>{p.points_scored}</td>
                    <td style={{ textAlign: 'center', color: '#7f8c8d' }}>{p.matches_played}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>Edit</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: '#e74c3c' }} onClick={() => handleDelete(p.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal animate-fadein">
            <h2>{editing ? 'Edit Player' : 'Add Player'}</h2>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>Full Name *</label>
                <input className="form-control" placeholder="Player name" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input className="form-control" type="email" placeholder="player@example.com" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input className="form-control" placeholder="+1 555 0000" value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Player'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AppDialog dialog={dialog} onClose={() => setDialog(null)} />
    </div>
  );
}
